import React, { useContext } from "react";

import { ImageOptions } from "@/common/Options/ImageOptions";
import { TextOptions } from "@/common/Options/TextOptions";
import {
  OptionEnum,
  OptionModeEnum,
  OptionTypeEnum,
} from "@/components/Option";
import { QuizContext } from "@/contexts";
import {
  InteractionLayout,
  Option,
  QuizResult,
  QuizVote,
} from "@/service/Model";

export type QuizOptionsProps = {
  interactionId: string;
  slideId: string;
  options: Option[];
  results: QuizResult[];
  isEnded?: boolean;
  layout: InteractionLayout;
  userVote?: QuizVote;
  onNext: (branch?: string) => void;
  showCorrectAnswer?: boolean;
};

export const QuizOptions: React.FC<QuizOptionsProps> = (
  props: QuizOptionsProps
): React.JSX.Element => {
  const quizContext = useContext(QuizContext);

  const {
    layout,
    slideId,
    interactionId,
    userVote,
    isEnded,
    options,
    showCorrectAnswer,
    onNext,
  } = props;

  const isVoted = !!userVote;

  const handleQuizVote = (vote: QuizVote, branch?: string): void => {
    if (isEnded || !quizContext) {
      return;
    }

    quizContext.onVote(vote).then(() => {
      onNext(branch);
    });
  };

  const getAnswer = (option: Option): boolean | undefined => {
    if (!userVote) {
      return undefined;
    }

    if (showCorrectAnswer && userVote.correctAnswers) {
      // Resolve correct answers
      return !!userVote.correctAnswers.find((o: Option) => o.id === option.id);
    }

    // Render as unselected option (blue)
    if (userVote.optionId !== option.id) {
      return undefined;
    }

    return userVote.answer;
  };

  const resolveOption = (option: Option): OptionEnum | undefined => {
    const answer = getAnswer(option);
    if (userVote) {
      if (answer) {
        return OptionEnum.correct;
      } else if (userVote.optionId === option.id) {
        return OptionEnum.wrong;
      }
    }
    return undefined;
  };

  const resolveOptionMode = (): OptionModeEnum => {
    return isVoted || isEnded ? OptionModeEnum.results : OptionModeEnum.normal;
  };

  if (layout === InteractionLayout.Text) {
    return (
      <TextOptions
        type={OptionTypeEnum.quiz}
        interactionId={interactionId}
        slideId={slideId}
        options={options}
        onVote={handleQuizVote}
        resolveOption={resolveOption}
        resolveOptionMode={resolveOptionMode}
        isClickable={!isVoted && !isEnded}
        isSecret={true}
      />
    );
  } else {
    return (
      <ImageOptions
        type={OptionTypeEnum.quiz}
        interactionId={interactionId}
        slideId={slideId}
        options={options}
        onVote={handleQuizVote}
        resolveOption={resolveOption}
        resolveOptionMode={resolveOptionMode}
        isClickable={!isVoted && !isEnded}
        isSecret={true}
      />
    );
  }
};
